<script lang="ts">
	import '../app.css';
	import { ConsentBanner } from '$lib/components/ui';
	import { BANNER_VERSION, readConsentCookie } from '$lib/consent';
	import { env } from '$env/dynamic/public';
	import { initialiseAnalyticsQueues, updateGoogleConsent } from '$lib/analytics';
	import { trackMarketingPage } from '$lib/marketing';
	import { afterNavigate } from '$app/navigation';
	import { page } from '$app/state';
	import { captureBrowserAttribution } from '$lib/attribution';

	let { children } = $props();

	// Safe default for SSR/prerendering and no-JS visitors (plan/09 §2): the
	// prerendered HTML can't know a real visitor's cookie at build time (plan/01
	// §3), so it always renders the banner. The mount effect below corrects this
	// once, client-side, against the real cookie — hiding it for anyone who has
	// already answered instead of re-nagging them (never the other way around,
	// so a first-time no-JS visitor always still sees a working banner).
	let showBanner = $state(true);
	// /links (the Instagram bio page) never asks: on a phone the banner covers
	// most of the link list, and Instagram's in-app browser rarely has our
	// cookie. Nothing loads there before consent anyway, and its links carry
	// utm_source=instagram, so the next page asks and still knows the source.
	const bannerDeferred = $derived(page.url.pathname === '/links');
	let analyticsLoaded = false;
	afterNavigate(() => trackMarketingPage());

	// Plausible is cookieless and cookie-consent-gated anyway (plan/01 §1,
	// plan/09 §1) — loaded only after Accept, and only if a domain is
	// configured at all (D8). Nothing third-party loads before that.
	function loadAnalytics(): void {
		updateGoogleConsent();
		trackMarketingPage();
		if (analyticsLoaded) return;
		initialiseAnalyticsQueues();
		const domain = env.PUBLIC_PLAUSIBLE_DOMAIN;
		if (domain) {
			const script = document.createElement('script');
			script.defer = true;
			script.dataset.domain = domain;
			script.src = 'https://mango.dignicy.com/js/script.outbound-links.pageview-props.revenue.js';
			document.head.appendChild(script);
		}

		const gtmId = env.PUBLIC_GTM_CONTAINER_ID;
		if (gtmId && /^GTM-[A-Z0-9]+$/.test(gtmId)) {
			window.dataLayer?.push({ 'gtm.start': Date.now(), event: 'gtm.js' });
			const gtm = document.createElement('script');
			gtm.async = true;
			gtm.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(gtmId)}`;
			document.head.appendChild(gtm);
		}
		analyticsLoaded = true;
	}

	// Sanctioned $effect use: a one-time browser-storage read + conditional
	// script injection (CLAUDE.md "browser APIs" / "analytics"). No cleanup
	// needed — it never subscribes to anything, just runs once on mount.
	$effect(() => {
		updateGoogleConsent();
		const existing = readConsentCookie();
		if (!existing) return; // no choice yet — keep showing the banner.
		showBanner = existing.banner_version !== BANNER_VERSION;
		if (existing.analytics) {
			captureBrowserAttribution();
			loadAnalytics();
		}
	});
</script>

<svelte:head>
	<link rel="icon" href="/favicon.png" type="image/png" sizes="512x512" />
	<link rel="icon" href="/favicon-32.png" sizes="32x32" type="image/png" />
	<link rel="apple-touch-icon" href="/apple-touch-icon.png" />
</svelte:head>

{@render children()}

{#if showBanner && !bannerDeferred}
	<ConsentBanner
		onaccept={() => {
			showBanner = false;
			captureBrowserAttribution();
			loadAnalytics();
		}}
		ondecline={() => {
			showBanner = false;
			updateGoogleConsent();
			if (analyticsLoaded) location.reload();
		}}
	/>
{/if}
